/**
 * API endpoint constants for the legaltech platform
 * 
 * All endpoints are relative to the base API URL (e.g., /api/v1)
 * Base URL is configured in api.ts via environment variable VITE_API_URL
 */

export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    ME: '/auth/me',
    PROFILE: '/auth/profile',
    REFRESH: '/auth/refresh',
    LOGOUT: '/auth/logout',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
  },

  // Users (internal user management)
  USERS: {
    byId: (id: number) => `/users/${id}`,
    CHANGE_PASSWORD: (id: number) => `/users/${id}/change-password`,
  },

  // Cases
  CASES: {
    BASE: '/cases',
    STATS: '/cases/stats',
    byId: (id: number) => `/cases/${id}`,
    restore: (id: number) => `/cases/${id}/restore`,
  },

  // Documents
  DOCUMENTS: {
    BASE: '/documents',
    UPLOAD: '/documents/upload',
    byId: (id: number) => `/documents/${id}`,
    restore: (id: number) => `/documents/${id}/restore`,
    versions: (documentId: number) => `/documents/${documentId}/versions`,
    versionDetail: (documentId: number, version: number) => `/documents/${documentId}/versions/${version}`,
    download: (documentId: number) => `/documents/${documentId}/download`,
    downloadVersion: (documentId: number, version: number) => `/documents/${documentId}/versions/${version}/download`,
  },

  // Deadlines
  DEADLINES: {
    BASE: '/deadlines',
    STATS: '/deadlines/stats',
    UPCOMING: '/deadlines/upcoming',
    OVERDUE: '/deadlines/overdue',
    byId: (id: number) => `/deadlines/${id}`,
    complete: (id: number) => `/deadlines/${id}/complete`,
    pending: (id: number) => `/deadlines/${id}/pending`,
  },

  // Notifications
  NOTIFICATIONS: {
    BASE: '/notifications',
    STATS: '/notifications/stats',
    MARK_ALL_READ: '/notifications/mark-all-read',
    PREFERENCES: '/notifications/preferences',
    BULK: '/notifications/bulk',
    byId: (id: number) => `/notifications/${id}`,
  },

  // Admin
  ADMIN: {
    STATS: '/admin/stats',
    USERS: {
      BASE: '/admin/users',
      byId: (id: number) => `/admin/users/${id}`,
      resetPassword: (id: number) => `/admin/users/${id}/reset-password`,
    },
    ROLES: {
      BASE: '/admin/roles',
      byId: (id: number) => `/admin/roles/${id}`,
    },
    CONTOURS: {
      BASE: '/admin/contours',
      byId: (id: number) => `/admin/contours/${id}`,
    },
    STORAGE_USAGE: '/admin/storage-usage',
    ACTIVITY_REPORT: '/admin/activity-report',
    HEALTH: '/admin/health',
    AUDIT_LOGS: {
      BASE: '/admin/audit-logs',
      EXPORT: '/admin/audit-logs/export',
    },
    BACKUPS: {
      BASE: '/admin/backups',
      byId: (id: string) => `/admin/backups/${id}`,
    },
  },

  // Client portal
  CLIENT: {
    AUTH: {
      LOGIN: '/client/auth/login',
      REFRESH: '/client/auth/refresh',
      PROFILE: '/client/auth/profile',
      CHANGE_PASSWORD: '/client/auth/change-password',
      LOGOUT: '/client/auth/logout',
    },
    PROFILE: '/client/profile',
    CASES: {
      BASE: '/client/cases',
      byId: (caseId: number) => `/client/cases/${caseId}`,
      documents: (caseId: number) => `/client/cases/${caseId}/documents`,
      timeline: (caseId: number) => `/client/cases/${caseId}/timeline`,
    },
    DEADLINES: '/client/deadlines',
    DOCUMENTS: {
      BASE: '/client/documents',
      download: (documentId: number) => `/client/documents/${documentId}/download`,
      UPLOAD: '/client/documents/upload',
    },
  },

  // Timeline
  TIMELINE: {
    BASE: '/timeline',
    byCaseId: (caseId: number) => `/timeline/case/${caseId}`,
  },

  // Workspace
  WORKSPACE: {
    BASE: '/workspace',
    byId: (id: number) => `/workspace/${id}`,
    documents: (workspaceId: number) => `/workspace/${workspaceId}/documents`,
  },

  // Search
  SEARCH: {
    GLOBAL: '/search',
    DOCUMENTS: '/search/documents',
    CASES: '/search/cases',
  },
} as const;

export type ApiEndpoints = typeof API_ENDPOINTS;